import React, { useEffect, useState } from 'react';
import {
  CalendarOff,
  Check,
  ExternalLink,
  Headphones,
  Link2Off,
  Loader,
  ShieldCheck,
  Timer,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../ui/Button';
import { useSpotify } from '../../contexts/SpotifyContext';
import { normalizeSpotifyPlaylistUri, startSpotifyConnect } from '../../services/spotifyAuth';
import SpotifyIcon from './SpotifyIcon';

function PreferenceToggle({
  checked,
  onChange,
  title,
  description,
  icon: Icon,
  testId,
  nested = false,
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      data-testid={testId}
      className={`w-full flex items-start gap-3 text-left rounded-xl px-3 py-3 transition-colors ${
        nested ? 'ml-2 border-l-2 border-secondary-200 dark:border-secondary-700 pl-4' : ''
      } ${
        checked
          ? 'bg-[#1DB954]/8 dark:bg-[#1DB954]/10'
          : 'hover:bg-secondary-50 dark:hover:bg-secondary-800/50'
      }`}
    >
      {Icon && (
        <span
          className={`mt-0.5 h-8 w-8 shrink-0 rounded-lg inline-flex items-center justify-center ${
            checked
              ? 'bg-[#1DB954]/15 text-[#1DB954]'
              : 'bg-secondary-100 text-secondary-500 dark:bg-secondary-800 dark:text-secondary-400'
          }`}
        >
          <Icon className="h-4 w-4" />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-secondary-900 dark:text-white">
          {title}
        </span>
        {description && (
          <span className="block text-xs text-secondary-500 dark:text-secondary-400 mt-0.5 leading-relaxed">
            {description}
          </span>
        )}
      </span>
      <span
        className={`relative mt-1 h-5 w-9 shrink-0 rounded-full transition-colors ${
          checked ? 'bg-[#1DB954]' : 'bg-secondary-300 dark:bg-secondary-600'
        }`}
        aria-hidden
      >
        <span
          className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0'
          }`}
        />
      </span>
    </button>
  );
}

const SpotifyConnect = () => {
  const {
    connected,
    isPremium,
    product,
    displayName,
    disconnect,
    loadingStatus,
    accountError,
    refreshStatus,
    timerSyncEnabled,
    setTimerSyncEnabled,
    meetingPauseEnabled,
    setMeetingPauseEnabled,
    autoResumeAfterMeeting,
    setAutoResumeAfterMeeting,
    focusPlaylistUri,
    setFocusPlaylistUri,
    calendarConnected,
  } = useSpotify();
  const [busy, setBusy] = useState(false);
  const [playlistDraft, setPlaylistDraft] = useState(focusPlaylistUri || '');

  // Always re-check when this page mounts (covers OAuth return without full reload).
  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  useEffect(() => {
    setPlaylistDraft(focusPlaylistUri || '');
  }, [focusPlaylistUri]);

  const handleConnect = async () => {
    setBusy(true);
    try {
      await startSpotifyConnect();
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err?.message || 'Failed to start Spotify connect';
      toast.error(detail);
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    setBusy(true);
    try {
      await disconnect();
      toast.success('Spotify disconnected');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to disconnect Spotify');
    } finally {
      setBusy(false);
    }
  };

  const handleSavePlaylist = () => {
    const normalized = normalizeSpotifyPlaylistUri(playlistDraft);
    setFocusPlaylistUri(normalized);
    setPlaylistDraft(normalized);
    toast.success(normalized ? 'Focus playlist saved' : 'Focus playlist cleared');
  };

  return (
    <div className="w-full space-y-6 animate-fade-in-up">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-11 w-11 rounded-full bg-[#1DB954] flex items-center justify-center shadow-sm flex-shrink-0">
            <SpotifyIcon className="h-6 w-6 text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-secondary-900 dark:text-white tracking-tight">
              Spotify
            </h1>
            <p className="text-sm text-secondary-500 dark:text-secondary-400">
              Listen while you work — header player, focus playlist, and sync
            </p>
          </div>
        </div>
        {connected && !loadingStatus && (
          <div className="flex flex-wrap items-center gap-2">
            <a
              href="https://www.spotify.com/account/overview/"
              target="_blank"
              rel="noreferrer"
              className="btn-secondary inline-flex items-center gap-1.5"
            >
              Manage
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <Button
              variant="danger"
              onClick={handleDisconnect}
              disabled={busy}
              data-testid="spotify-disconnect"
            >
              <Link2Off className="h-4 w-4" />
              Disconnect
            </Button>
          </div>
        )}
      </div>

      {loadingStatus ? (
        <div className="card flex items-center justify-center gap-2 py-16 text-secondary-500">
          <Loader className="h-4 w-4 animate-spin" />
          <span className="text-sm">Checking connection…</span>
        </div>
      ) : connected ? (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
          {/* Account */}
          <aside className="lg:col-span-4 space-y-4">
            <div className="card overflow-hidden !p-0">
              <div className="px-5 py-6 bg-gradient-to-br from-[#1DB954]/15 via-[#1DB954]/5 to-transparent dark:from-[#1DB954]/20">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-medium uppercase tracking-wide text-secondary-500 dark:text-secondary-400">
                      Account
                    </p>
                    <p className="mt-1 text-lg font-semibold text-secondary-900 dark:text-white truncate">
                      {displayName || 'Connected'}
                    </p>
                    <p className="text-sm text-secondary-500 dark:text-secondary-400 capitalize">
                      {product || 'account'}
                      {isPremium ? ' · Premium' : ''}
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-[#1DB954]/15 text-[#1DB954] flex-shrink-0">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Connected
                  </span>
                </div>
              </div>
              <div className="px-5 py-4 space-y-2 border-t border-secondary-100 dark:border-secondary-700/60">
                {!isPremium && (
                  <p
                    className="text-xs text-amber-700 dark:text-amber-300"
                    data-testid="spotify-premium-upgrade"
                  >
                    Spotify Premium is needed for in-app playback.
                  </p>
                )}
                {accountError && (
                  <p className="text-xs text-red-600 dark:text-red-300">{accountError}</p>
                )}
                {isPremium && !accountError && (
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 leading-relaxed">
                    Open Spotify on a phone or computer — Tick Sync remote-controls that
                    device from the header player.
                  </p>
                )}
              </div>
            </div>
          </aside>

          {/* Preferences + Focus */}
          <div className="lg:col-span-8 space-y-4">
            <section className="card !p-0 overflow-hidden">
              <div className="px-5 py-3 border-b border-secondary-100 dark:border-secondary-700/60">
                <p className="text-sm font-semibold text-secondary-900 dark:text-white">
                  Playback preferences
                </p>
                <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5">
                  Optional automations that keep music in sync with your workday
                </p>
              </div>
              <div className="p-2 space-y-0.5">
                <PreferenceToggle
                  icon={Timer}
                  checked={timerSyncEnabled}
                  onChange={setTimerSyncEnabled}
                  title="Sync with timer"
                  description="Play when you start tracking; pause when you stop."
                  testId="spotify-timer-sync"
                />
                {calendarConnected && (
                  <>
                    <PreferenceToggle
                      icon={CalendarOff}
                      checked={meetingPauseEnabled}
                      onChange={setMeetingPauseEnabled}
                      title="Pause during meetings"
                      description="Auto-pause when an Outlook meeting starts."
                      testId="spotify-meeting-pause"
                    />
                    {meetingPauseEnabled && (
                      <PreferenceToggle
                        nested
                        checked={autoResumeAfterMeeting}
                        onChange={setAutoResumeAfterMeeting}
                        title="Resume after meeting"
                        description="Pick up where you left off when the meeting ends."
                        testId="spotify-meeting-resume"
                      />
                    )}
                  </>
                )}
              </div>
            </section>

            <section className="card !p-0 overflow-hidden">
              <div className="px-5 py-3 border-b border-secondary-100 dark:border-secondary-700/60 flex items-center gap-2">
                <Headphones className="h-4 w-4 text-[#1DB954]" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-secondary-900 dark:text-white">
                    Focus playlist
                  </p>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5">
                    One-click start from the header player
                  </p>
                </div>
                {focusPlaylistUri && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#1DB954] bg-[#1DB954]/10 px-2 py-0.5 rounded-full flex-shrink-0">
                    <Check className="h-3 w-3" />
                    Saved
                  </span>
                )}
              </div>
              <div className="p-5 space-y-3">
                <p className="text-sm text-secondary-500 dark:text-secondary-400 leading-relaxed">
                  Paste a Spotify playlist link or URI. Then use{' '}
                  <span className="font-medium text-secondary-700 dark:text-secondary-200">
                    Play focus playlist
                  </span>{' '}
                  in the expanded header player.
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    id="spotify-focus-playlist"
                    type="text"
                    value={playlistDraft}
                    onChange={(e) => setPlaylistDraft(e.target.value)}
                    placeholder="open.spotify.com/playlist/… or spotify:playlist:…"
                    className="flex-1 min-w-0 rounded-lg border border-secondary-200 dark:border-secondary-600 bg-secondary-50/50 dark:bg-secondary-900/40 px-3 py-2.5 text-sm text-secondary-900 dark:text-white placeholder:text-secondary-400 focus:outline-none focus:ring-2 focus:ring-[#1DB954]/40 focus:border-[#1DB954]/50"
                    data-testid="spotify-focus-playlist-input"
                  />
                  <Button
                    variant="secondary"
                    onClick={handleSavePlaylist}
                    data-testid="spotify-focus-playlist-save"
                    className="!px-5 sm:self-stretch"
                  >
                    Save
                  </Button>
                </div>
                {focusPlaylistUri && (
                  <p className="text-xs text-secondary-400 dark:text-secondary-500 font-mono truncate">
                    {focusPlaylistUri}
                  </p>
                )}
              </div>
            </section>
          </div>
        </div>
      ) : (
        <div className="card overflow-hidden !p-0">
          <div className="px-6 py-16 sm:py-20 flex flex-col items-center text-center gap-5 bg-gradient-to-b from-[#1DB954]/10 to-transparent dark:from-[#1DB954]/15">
            <div className="h-16 w-16 rounded-full bg-[#1DB954] flex items-center justify-center shadow-sm">
              <SpotifyIcon className="h-9 w-9 text-white" />
            </div>
            <div className="max-w-md">
              <h2 className="text-xl font-semibold text-secondary-900 dark:text-white">
                Connect Spotify
              </h2>
              <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-2 leading-relaxed">
                Control playback from Tick Sync, share what you&apos;re listening to in
                Messages, and optionally sync music with your timer.
              </p>
            </div>
            <Button
              variant="primary"
              onClick={handleConnect}
              disabled={busy}
              className="!bg-[#1DB954] hover:!bg-[#1ed760] !border-transparent !text-white shadow-sm !px-6"
              data-testid="spotify-connect"
            >
              {busy ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : (
                <SpotifyIcon className="h-4 w-4 text-white" />
              )}
              Connect Spotify
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpotifyConnect;
